import React, { useEffect, useMemo, useState } from "react";
import ClientFormDialog from "../components/templates/ClientFormDialog";
import DeleteClientDialog from "../components/organisms/DeleteClientDialog";
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Chip,
  IconButton,
  Button,
  Divider,
  TextField,
  MenuItem,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";

import { useOutletContext } from "react-router-dom";
import ClientTimeline from "../components/ClientTimeline";

import { API_BASE_URL, API_ENDPOINTS } from "../config";
import { getToken } from "../utils/auth";

// Core endpoints using centralized config
const CLIENTS_API_URL = `${API_BASE_URL}${API_ENDPOINTS.CLIENTS}`;
const DEALS_API_URL = `${API_BASE_URL}${API_ENDPOINTS.DEALS}`;
const CHAT_API_URL = `${API_BASE_URL}${API_ENDPOINTS.CHAT}`;

// Export for other modules if needed
export { CLIENTS_API_URL, DEALS_API_URL, CHAT_API_URL };

// Upload / template endpoints
const CLIENT_IMPORT_URL = `${API_BASE_URL}${API_ENDPOINTS.CLIENTS_IMPORT}`;
const CLIENT_TEMPLATE_URL = `${API_BASE_URL}${API_ENDPOINTS.CLIENTS_TEMPLATE}`;

const STAGES = [
  "New Lead",
  "Active",
  "Under Contract",
  "Closed",
  "Lost",
  "Unspecified",
];

const DEAL_STAGES = ["Lead", "Qualified", "Proposal Sent", "Verbal Yes", "Won", "Lost"];
const DEAL_STATUS = ["Open", "Won", "Lost"];

// ---------- auth helper (JWT from login) ----------
const getAuthHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function getStageChipProps(stage) {
  const label = stage || "Unspecified";
  switch (stage) {
    case "Closed":
      return { label, color: "success" };
    case "Under Contract":
      return { label, color: "primary" };
    case "New Lead":
      return { label, color: "warning" };
    case "Lost":
      return { label, color: "default" };
    case "Active":
      return { label, color: "info" };
    default:
      return { label: "Unspecified", color: "default" };
  }
}

function formatDate(value) {
  if (!value) return null;
  // API sends plain YYYY-MM-DD; parsing that as a Date would shift it a day in
  // negative-offset timezones, so format the parts directly.
  const [year, month, day] = String(value).slice(0, 10).split("-");
  if (!year || !month || !day) return null;
  return `${month}/${day}/${year}`;
}

/** One labelled field of the client profile. */
function ClientField({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        color={value ? "text.primary" : "text.secondary"}
        sx={{ overflowWrap: "anywhere" }}
      >
        {value || "—"}
      </Typography>
    </Grid>
  );
}

export default function VendorDashboard() {
  // This is now the AgentBlayna CRM Client + Deals screen
  const theme = useTheme();
  const { onAiStatusChange, aiRefreshToken } = useOutletContext() || {};

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stageFilter, setStageFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("recent");
  const [error, setError] = useState("");

  const [selectedClientId, setSelectedClientId] = useState(null);

  // Client CRUD dialogs
  const [clientFormOpen, setClientFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingClient, setDeletingClient] = useState(null);

  // Deals state
  const [deals, setDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [dealsError, setDealsError] = useState("");
  const [dealDialogOpen, setDealDialogOpen] = useState(false);
  const [dealSaving, setDealSaving] = useState(false);
  const [dealForm, setDealForm] = useState({
    title: "",
    description: "",
    value: "",
    stage: "Lead",
    status: "Open",
    expected_close_date: "",
    commission_rate: "",
    listing_date: "",
    photography_scheduled_at: "",
    open_house_at: "",
    open_house_end_at: "",
    close_date: "",
  });
  const [editingDealId, setEditingDealId] = useState(null);

  // AI insight states
  const [aiInsight, setAiInsight] = useState("");
  const [pipelineForecast, setPipelineForecast] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");

  // upload state
  const [uploading, setUploading] = useState(false);

  // ------------ LOAD CLIENTS ------------

  const loadClients = async () => {
    try {
      setLoading(true);
      setError("");
      onAiStatusChange?.({ loading: true });

      const res = await fetch(CLIENTS_API_URL, {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });
      if (!res.ok) {
        throw new Error(`Failed to load clients (${res.status})`);
      }
      const data = await res.json();

      const normalized = (data || []).map((c) => {
        const name = `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim();
        return {
          id: c.id,
          name: name || "Unnamed client",
          first_name: c.first_name,
          last_name: c.last_name,
          email: c.email,
          phone: c.phone,
          client_type: c.client_type,
          lead_source: c.lead_source,
          stage: c.stage || "New Lead",
          notes: c.notes,
          birthday: c.birthday,
          anniversary: c.anniversary,
          created_at: c.created_at,
          updated_at: c.updated_at,
          _raw: c, // original API response for edit form
        };
      });

      setClients(normalized);

      // If no client is selected yet, pick the first one
      if (!selectedClientId && normalized.length > 0) {
        setSelectedClientId(normalized[0].id);
      }

      onAiStatusChange?.({
        loading: false,
        lastUpdated: new Date().toISOString(),
      });
    } catch (err) {
      console.error("Error fetching clients:", err);
      setError(err.message || "Failed to load clients.");
      onAiStatusChange?.({ loading: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiRefreshToken, onAiStatusChange]);

  // When clients or filters change, make sure selected client is still visible
  useEffect(() => {
    if (!clients.length) {
      setSelectedClientId(null);
      return;
    }
    const exists = clients.some((c) => c.id === selectedClientId);
    if (!exists) {
      setSelectedClientId(clients[0].id);
    }
  }, [clients, selectedClientId]);

  // ------------ FILTER + SORT CLIENTS ------------

  const filteredClients = useMemo(() => {
    let list = [...clients];

    if (stageFilter !== "all") {
      list = list.filter((c) => (c.stage || "Unspecified") === stageFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter((c) => {
        return (
          (c.name || "").toLowerCase().includes(term) ||
          (c.email || "").toLowerCase().includes(term) ||
          (c.phone || "").toLowerCase().includes(term)
        );
      });
    }

    list.sort((a, b) => {
      switch (sortKey) {
        case "recent":
          return (
            new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
          );
        case "name_asc":
          return (a.name || "").localeCompare(b.name || "");
        case "name_desc":
          return (b.name || "").localeCompare(a.name || "");
        case "stage":
          return (a.stage || "").localeCompare(b.stage || "");
        default:
          return 0;
      }
    });

    return list;
  }, [clients, stageFilter, searchTerm, sortKey]);

  const selectedClient = useMemo(
    () => filteredClients.find((c) => c.id === selectedClientId) || null,
    [filteredClients, selectedClientId]
  );

  // ------------ KPI SUMMARY ------------

  const summary = useMemo(() => {
    if (clients.length === 0) {
      return {
        total: 0,
        active: 0,
        newLeads: 0,
        underContract: 0,
      };
    }

    const total = clients.length;
    const active = clients.filter((c) => c.stage && !["Closed", "Lost"].includes(c.stage)).length;
    const newLeads = clients.filter((c) => c.stage === "New Lead").length;
    const underContract = clients.filter((c) => c.stage === "Under Contract").length;

    return { total, active, newLeads, underContract };
  }, [clients]);

  // ---------- DEALS: LOAD FOR SELECTED CLIENT ----------

  const loadDealsForClient = async (clientId) => {
    if (!clientId) {
      setDeals([]);
      return;
    }
    try {
      setDealsLoading(true);
      setDealsError("");
      const res = await fetch(`${DEALS_API_URL}?client_id=${clientId}`, {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });
      if (!res.ok) {
        throw new Error(`Failed to load deals (${res.status})`);
      }
      const data = await res.json();
      setDeals(data || []);
    } catch (err) {
      console.error("Error loading deals:", err);
      setDealsError(err.message || "Failed to load deals.");
    } finally {
      setDealsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedClientId) {
      loadDealsForClient(selectedClientId);
    } else {
      setDeals([]);
    }
  }, [selectedClientId]);

  // ---------- EXPORT CLIENTS ----------

  const handleDownloadCsv = () => {
    if (clients.length === 0) return;

    const headers = [
      "First Name",
      "Last Name",
      "Full Name",
      "Email",
      "Phone",
      "Client Type",
      "Lead Source",
      "Stage",
      "Created At",
    ];

    const rows = clients.map((c) => [
      c.first_name || "",
      c.last_name || "",
      c.name || "",
      c.email || "",
      c.phone || "",
      c.client_type || "",
      c.lead_source || "",
      c.stage || "",
      c.created_at || "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "agentblayna_clients_snapshot.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ---------- IMPORT CLIENTS (CSV / EXCEL) ----------

  const handleDownloadTemplate = async () => {
    try {
      const res = await fetch(CLIENT_TEMPLATE_URL, {
        headers: {
          ...getAuthHeaders(),
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "agentblayna_clients_template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Template download error:", err);
      alert("Could not download template. Check backend /api/clients/template.");
    }
  };

  const handleUploadClients = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls,.csv";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);

        const res = await fetch(CLIENT_IMPORT_URL, {
          method: "POST",
          headers: {
            ...getAuthHeaders(), // no Content-Type here, browser sets it for FormData
          },
          body: fd,
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `HTTP ${res.status}`);
        }

        const data = await res.json().catch(() => ({}));
        const imported = data.imported ?? data.count ?? null;

        alert(imported != null ? `Imported ${imported} clients successfully.` : "Import finished.");

        await loadClients();
      } catch (err) {
        console.error("Import error:", err);
        alert(err?.message || "Import failed. Make sure the file matches the template.");
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  // ---------- AI INSIGHTS + PIPELINE FORECAST ----------

  const buildClientSnapshotForAi = () => {
    if (!clients.length) return "No client data is currently loaded.";

    const simple = clients.map((c) => ({
      name: c.name,
      stage: c.stage,
      lead_source: c.lead_source,
      client_type: c.client_type,
      created_at: c.created_at,
    }));

    return JSON.stringify(simple).slice(0, 8000);
  };

  const callChatApi = async (userPrompt) => {
    setAiBusy(true);
    setAiError("");
    onAiStatusChange?.({ loading: true });

    try {
      const snapshot = buildClientSnapshotForAi();

      const messages = [
        {
          role: "system",
          content:
            "You are AgentBlayna, an AI CRM copilot for real estate agents and small businesses. " +
            "You are given a JSON list of clients with fields: name, stage, lead_source, client_type, created_at. " +
            "Use ONLY that data when referring to specific counts or categories. " +
            "Speak like a top-producing agent's operations partner: clear, direct, practical. " +
            "Avoid markdown bullets. Use short paragraphs or numbered lists only when helpful.\n\n" +
            "Client snapshot:\n" +
            snapshot,
        },
        { role: "user", content: userPrompt },
      ];

      const res = await fetch(CHAT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ messages, model: "gpt-4o-mini" }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const reply =
        typeof data?.reply === "string"
          ? data.reply
          : typeof data?.reply?.content === "string"
          ? data.reply.content
          : "I couldn’t generate an AI suggestion. Please try again.";

      return reply;
    } catch (err) {
      console.error("AI error:", err);
      setAiError("AgentBlayna couldn’t reach the AI engine. Check the backend and try again.");
      return "";
    } finally {
      setAiBusy(false);
      onAiStatusChange?.({ loading: false, lastUpdated: new Date().toISOString() });
    }
  };

  const handleGenerateAiPlan = async () => {
    if (!clients.length) {
      setAiInsight("No clients found. Add a few leads and clients to generate a follow-up plan.");
      return;
    }

    const prompt =
      "Look at the client snapshot and tell me who I should talk to today. " +
      "Group them into: 1) Hot right now, 2) Nurture over the next 30 days, 3) Closed or archived. " +
      "Be specific and concise so I can use this as my call list for the day.";

    const text = await callChatApi(prompt);
    if (text) setAiInsight(text);
  };

  const handleGeneratePipelineForecast = async () => {
    if (!clients.length) {
      setPipelineForecast("No clients loaded, so there is nothing to forecast yet.");
      return;
    }

    const prompt =
      "Using the client snapshot, forecast my next 30 days of pipeline. " +
      "Summarize: how many deals look close to converting, which lead sources are working best, " +
      "and where I’m likely leaving money on the table. Keep it short and tactical.";

    const text = await callChatApi(prompt);
    if (text) setPipelineForecast(text);
  };

  const handleEmailSummary = async () => {
    const base =
      "Here’s a quick client pipeline summary from AgentBlayna. Paste this into an email or your daily focus notes.\n\n";

    const body =
      base +
      (aiInsight || "Generate the daily focus plan with AI first.") +
      (pipelineForecast ? "\n\nPipeline forecast:\n" + pipelineForecast : "");

    try {
      await navigator.clipboard.writeText(body);
      alert("AI summary copied to clipboard. Paste it wherever you need it.");
    } catch {
      alert("Could not copy to clipboard, but here’s your summary:\n\n" + body);
    }
  };

  // ---------- DEALS: CREATE & DELETE ----------

  const openNewDealDialog = () => {
    if (!selectedClient) {
      alert("Select a client first.");
      return;
    }
    setEditingDealId(null);
    setDealForm({
      title: "",
      description: "",
      value: "",
      stage: "Lead",
      status: "Open",
      expected_close_date: "",
      commission_rate: "",
      listing_date: "",
      photography_scheduled_at: "",
      open_house_at: "",
      open_house_end_at: "",
      close_date: "",
    });
    setDealDialogOpen(true);
  };

  const handleChangeDealForm = (field) => (e) => {
    setDealForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const openEditDealDialog = (deal) => {
    setEditingDealId(deal.id);
    setDealForm({
      title: deal.title || "",
      description: deal.description || "",
      value: deal.value != null ? String(deal.value) : "",
      stage: deal.stage || "Lead",
      status: deal.status || "Open",
      expected_close_date: deal.expected_close_date || "",
      commission_rate: deal.commission_rate != null ? String(Number(deal.commission_rate) * 100) : "",
      listing_date: deal.listing_date || "",
      photography_scheduled_at: deal.photography_scheduled_at ? deal.photography_scheduled_at.slice(0, 16) : "",
      open_house_at: deal.open_house_at ? deal.open_house_at.slice(0, 16) : "",
      open_house_end_at: deal.open_house_end_at ? deal.open_house_end_at.slice(0, 16) : "",
      close_date: deal.close_date || "",
    });
    setDealDialogOpen(true);
  };

  const handleSaveDeal = async () => {
    if (!selectedClient) return;
    if (!dealForm.title) return;

    try {
      setDealSaving(true);
      setDealsError("");

      const payload = {
        client_id: selectedClient.id,
        title: dealForm.title,
        description: dealForm.description || null,
        value: dealForm.value ? Number(dealForm.value) : null,
        stage: dealForm.stage || null,
        status: dealForm.status || null,
        expected_close_date: dealForm.expected_close_date || null,
        commission_rate: dealForm.commission_rate ? Number(dealForm.commission_rate) / 100 : null,
        listing_date: dealForm.listing_date || null,
        photography_scheduled_at: dealForm.photography_scheduled_at || null,
        open_house_at: dealForm.open_house_at || null,
        open_house_end_at: dealForm.open_house_end_at || null,
        close_date: dealForm.close_date || null,
      };

      const isEdit = editingDealId != null;
      const url = isEdit ? `${DEALS_API_URL}${editingDealId}` : DEALS_API_URL;
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`Failed to ${isEdit ? "update" : "create"} deal (${res.status})`);

      await loadDealsForClient(selectedClient.id);
      setDealDialogOpen(false);
    } catch (err) {
      console.error("Error saving deal:", err);
      setDealsError(err.message || "Failed to save deal.");
    } finally {
      setDealSaving(false);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm("Delete this deal?")) return;

    try {
      setDealsError("");
      const res = await fetch(`${DEALS_API_URL}${dealId}`, {
        method: "DELETE",
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`Failed to delete deal (${res.status})`);
      setDeals((prev) => prev.filter((d) => d.id !== dealId));
    } catch (err) {
      console.error("Error deleting deal:", err);
      setDealsError(err.message || "Failed to delete deal.");
    }
  };

  return (
    <Box sx={{ p: 1, pb: 4 }}>
      {/* Header row */}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={1.5} mb={2}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <AssessmentRoundedIcon fontSize="medium" sx={{ color: theme.palette.primary.main, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={800} noWrap>
              Client Pipeline
            </Typography>
            <Typography variant="body2" color="text.secondary">
              AgentBlayna CRM · One view of every lead, client, and deal.
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} sx={{ flexShrink: 0 }}>
          <Tooltip title="Add a single client manually">
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<AddRoundedIcon />}
              onClick={() => { setEditingClient(null); setClientFormOpen(true); }}
            >
              Add Client
            </Button>
          </Tooltip>
          <Tooltip title="Download an Excel template for bulk import">
            <Button size="small" variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={handleDownloadTemplate}>
              Template
            </Button>
          </Tooltip>
          <Tooltip title="Import clients from CSV or Excel">
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<UploadFileRoundedIcon />}
              onClick={handleUploadClients}
              disabled={uploading}
            >
              {uploading ? "Importing…" : "Import"}
            </Button>
          </Tooltip>
          <Tooltip title="Download a simple CSV snapshot of your clients">
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadRoundedIcon />}
              onClick={handleDownloadCsv}
              disabled={clients.length === 0}
            >
              Export
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        {/* Left column – KPIs + filters + client list */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            {/* KPI cards */}
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Contacts
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {summary.total}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Every lead and client in AgentBlayna.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Active Pipeline
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {summary.active}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Not closed and not lost.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    New Leads
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {summary.newLeads}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    People who need a first touch.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Under Contract
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {summary.underContract}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Deals in motion right now.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Filters */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Filters
                </Typography>
                <TextField
                  size="small"
                  label="Search client"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                />
                <TextField
                  size="small"
                  select
                  label="Stage"
                  value={stageFilter}
                  onChange={(e) => setStageFilter(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="all">All stages</MenuItem>
                  {STAGES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  select
                  label="Sort by"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="recent">Most recent</MenuItem>
                  <MenuItem value="name_asc">Name (A → Z)</MenuItem>
                  <MenuItem value="name_desc">Name (Z → A)</MenuItem>
                  <MenuItem value="stage">Stage</MenuItem>
                </TextField>
              </Stack>
            </Paper>

            {/* Client list */}
            <Paper sx={{ p: 1.5, maxHeight: 420, overflowY: "auto", borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Clients ({filteredClients.length})
                </Typography>
                <Tooltip title="Have AgentBlayna build your daily call list">
                  <IconButton size="small" onClick={handleGenerateAiPlan}>
                    <AutoFixHighRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Divider sx={{ mb: 1 }} />
              {loading && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  Loading client data…
                </Typography>
              )}
              {!loading && error && (
                <Typography variant="body2" color="error" sx={{ p: 1 }}>
                  {error}
                </Typography>
              )}
              {!loading && !error && filteredClients.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  No clients match the current filters.
                </Typography>
              )}
              <List dense disablePadding>
                {filteredClients.map((c) => {
                  const chipProps = getStageChipProps(c.stage);
                  const isSelected = c.id === selectedClientId;
                  return (
                    <React.Fragment key={c.id}>
                      <ListItem
                        button
                        selected={isSelected}
                        onClick={() => setSelectedClientId(c.id)}
                        sx={{ py: 0.75 }}
                      >
                        <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 1 }}>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                              <Typography variant="body2" fontWeight={isSelected ? 700 : 600} noWrap>
                                {c.name}
                              </Typography>
                              {c.lead_source && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ textTransform: "uppercase" }}
                                >
                                  {c.lead_source}
                                </Typography>
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {c.email || "No email on file"}
                              {c.phone ? ` • ${c.phone}` : ""}
                            </Typography>
                          </Box>
                          <Chip size="small" {...chipProps} sx={{ flexShrink: 0 }} />
                        </Box>
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          </Stack>
        </Grid>

        {/* Right column – pipeline snapshot (reworked) + timeline + deals + AI insights + explanation */}
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            {/* Client profile — realtors open a client to see the person,
                not aggregate pipeline stats. */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              {!selectedClient ? (
                <Typography variant="body2" color="text.secondary">
                  Select a client from the list to see their details.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ flexWrap: "wrap", gap: 1 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {selectedClient.name}
                      </Typography>
                      <Chip size="small" {...getStageChipProps(selectedClient.stage)} />
                    </Stack>
                    <Button
                      size="small"
                      startIcon={<EditRoundedIcon />}
                      onClick={() => {
                        setEditingClient(selectedClient._raw);
                        setClientFormOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                  </Stack>

                  <Grid container spacing={1.25}>
                    <ClientField label="Email" value={selectedClient.email} />
                    <ClientField label="Phone" value={selectedClient.phone} />
                    <ClientField label="Client type" value={selectedClient.client_type} />
                    <ClientField label="Lead source" value={selectedClient.lead_source} />
                    <ClientField label="Birthday" value={formatDate(selectedClient.birthday)} />
                    <ClientField label="Anniversary" value={formatDate(selectedClient.anniversary)} />
                  </Grid>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.25 }}
                    >
                      Notes
                    </Typography>
                    <Typography
                      variant="body2"
                      color={selectedClient.notes ? "text.primary" : "text.secondary"}
                      sx={{ whiteSpace: "pre-wrap" }}
                    >
                      {selectedClient.notes || "No notes yet. Use Edit to add some."}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Paper>

            {/* Client timeline – what actually happened with this person */}
            <ClientTimeline clientId={selectedClientId} />

            {/* Deals panel for selected client */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Deals
                    {selectedClient ? ` · ${selectedClient.name}` : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Track the actual opportunities tied to this relationship.
                  </Typography>
                </Box>
                {selectedClient && (
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Edit client">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditRoundedIcon />}
                        onClick={() => { setEditingClient(selectedClient._raw); setClientFormOpen(true); }}
                      >
                        Edit
                      </Button>
                    </Tooltip>
                    <Tooltip title="Delete client">
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteRoundedIcon />}
                        onClick={() => { setDeletingClient(selectedClient._raw); setDeleteDialogOpen(true); }}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </Stack>
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddRoundedIcon />}
                  onClick={openNewDealDialog}
                  disabled={!selectedClient}
                >
                  New Deal
                </Button>
              </Stack>

              {!selectedClient && (
                <Typography variant="body2" color="text.secondary">
                  Select a client on the left to see and manage their deals.
                </Typography>
              )}

              {selectedClient && dealsLoading && (
                <Typography variant="body2" color="text.secondary">
                  Loading deals…
                </Typography>
              )}

              {selectedClient && dealsError && (
                <Typography variant="body2" color="error">
                  {dealsError}
                </Typography>
              )}

              {selectedClient && !dealsLoading && !dealsError && deals.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No deals yet for this client. Click “New Deal” to add your first opportunity.
                </Typography>
              )}

              {selectedClient && !dealsLoading && deals.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {deals.map((d) => {
                    const commRate = d.commission_rate != null ? Number(d.commission_rate) * 100 : null;
                    const estComm = d.estimated_commission != null
                      ? Number(d.estimated_commission)
                      : (d.value && d.commission_rate ? Number(d.value) * Number(d.commission_rate) : null);
                    const dates = [
                      d.listing_date && `Listed: ${new Date(d.listing_date).toLocaleDateString()}`,
                      d.photography_scheduled_at && `Photo: ${new Date(d.photography_scheduled_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}`,
                      d.open_house_at && `Open house: ${new Date(d.open_house_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}`,
                      d.close_date && `Closed: ${new Date(d.close_date).toLocaleDateString()}`,
                      d.expected_close_date && !d.close_date && `Est. close: ${new Date(d.expected_close_date).toLocaleDateString()}`,
                    ].filter(Boolean);
                    return (
                      <React.Fragment key={d.id}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ py: 0.75 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {d.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {d.stage || "No stage"} {d.status ? `• ${d.status}` : ""}
                              {d.value != null ? ` • $${Number(d.value).toLocaleString()}` : ""}
                              {commRate != null ? ` • ${commRate.toFixed(1)}%` : ""}
                              {estComm != null ? ` • Est. comm: $${estComm.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : ""}
                            </Typography>
                            {dates.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                {dates.join(" · ")}
                              </Typography>
                            )}
                          </Box>
                          <Stack direction="row" spacing={0}>
                            <IconButton size="small" onClick={() => openEditDealDialog(d)}>
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteDeal(d.id)}>
                              <DeleteRoundedIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                        <Divider />
                      </React.Fragment>
                    );
                  })}
                </Box>
              )}
            </Paper>

            {/* AI Insights card */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                AI Daily Focus & Pipeline Insights
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                AgentBlayna reads your stages, lead sources, and recency to tell you exactly who to talk to first, what
                to nurture, and where deals are about to slip through the cracks.
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <Button
                  variant="contained"
                  startIcon={<AutoFixHighRoundedIcon />}
                  onClick={handleGenerateAiPlan}
                  disabled={aiBusy}
                >
                  Today&apos;s Call List
                </Button>
                <Button variant="outlined" onClick={handleGeneratePipelineForecast} disabled={aiBusy}>
                  Pipeline Forecast
                </Button>
                <Button variant="outlined" onClick={handleEmailSummary} disabled={aiBusy && !aiInsight && !pipelineForecast}>
                  Copy Summary
                </Button>
              </Stack>

              {aiBusy && (
                <Typography variant="body2" color="text.secondary">
                  AgentBlayna is analyzing your book of business…
                </Typography>
              )}

              {!aiBusy && aiError && (
                <Typography variant="body2" color="error">
                  {aiError}
                </Typography>
              )}

              {!aiBusy && !aiError && (aiInsight || pipelineForecast) && (
                <Box sx={{ mt: 1 }}>
                  {aiInsight && (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                      {aiInsight}
                    </Typography>
                  )}
                  {pipelineForecast && (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-line", mt: aiInsight ? 1.5 : 0 }}>
                      {pipelineForecast}
                    </Typography>
                  )}
                </Box>
              )}

              {!aiBusy && !aiError && !aiInsight && !pipelineForecast && (
                <Typography variant="body2" color="text.secondary">
                  Click “Today&apos;s Call List” to generate a short, tactical plan you can use to run your day like a
                  top producer.
                </Typography>
              )}
            </Paper>

            {/* Explanation */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <WarningAmberRoundedIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  How AgentBlayna thinks about your pipeline
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" paragraph>
                Instead of burying you in fields and automations, AgentBlayna focuses on the basics that move the
                needle: who&apos;s in your world, where they are in the journey, and what to do next. Stages like New
                Lead, Active, Under Contract, Closed, and Lost are enough to run a tight, high-output business.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Deals tie real dollars to those relationships, and the client timeline keeps a running history of every
                touchpoint and stage change. As you add deals, tasks, and notes, AgentBlayna becomes a living command
                center: one screen that keeps you honest about your follow-up, protects your future closings, and
                replaces overbuilt CRMs that charge more than some people&apos;s car payment.
              </Typography>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {/* New / Edit Deal Dialog */}
      <Dialog
        open={dealDialogOpen}
        onClose={() => !dealSaving && setDealDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingDealId ? "Edit Deal" : "New Deal"}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {!selectedClient && (
            <Typography variant="body2" color="text.secondary">
              Select a client first.
            </Typography>
          )}
          {selectedClient && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Client: <strong>{selectedClient.name}</strong>
              </Typography>
              <Grid container spacing={2}>
                {/* Basic info */}
                <Grid item xs={12}>
                  <TextField
                    label="Deal title"
                    fullWidth
                    required
                    value={dealForm.title}
                    onChange={handleChangeDealForm("title")}
                    placeholder="123 Main St listing, Buyer rep, Referral from Sarah, etc."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    minRows={2}
                    value={dealForm.description}
                    onChange={handleChangeDealForm("description")}
                  />
                </Grid>

                {/* Value + Commission */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Value ($)"
                    fullWidth
                    type="number"
                    value={dealForm.value}
                    onChange={handleChangeDealForm("value")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Commission rate (%)"
                    fullWidth
                    type="number"
                    inputProps={{ step: "0.1", min: "0", max: "100" }}
                    value={dealForm.commission_rate}
                    onChange={handleChangeDealForm("commission_rate")}
                    placeholder="e.g. 3"
                  />
                </Grid>

                {/* Stage + Status */}
                <Grid item xs={12} sm={6}>
                  <TextField select label="Stage" fullWidth value={dealForm.stage} onChange={handleChangeDealForm("stage")}>
                    {DEAL_STAGES.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select label="Status" fullWidth value={dealForm.status} onChange={handleChangeDealForm("status")}>
                    {DEAL_STATUS.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Key dates */}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    KEY DATES
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Listing date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={dealForm.listing_date}
                    onChange={handleChangeDealForm("listing_date")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Photography"
                    type="datetime-local"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={dealForm.photography_scheduled_at}
                    onChange={handleChangeDealForm("photography_scheduled_at")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Open house start"
                    type="datetime-local"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={dealForm.open_house_at}
                    onChange={handleChangeDealForm("open_house_at")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Open house end"
                    type="datetime-local"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={dealForm.open_house_end_at}
                    onChange={handleChangeDealForm("open_house_end_at")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Expected close date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={dealForm.expected_close_date}
                    onChange={handleChangeDealForm("expected_close_date")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Actual close date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={dealForm.close_date}
                    onChange={handleChangeDealForm("close_date")}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !dealSaving && setDealDialogOpen(false)} disabled={dealSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveDeal} disabled={dealSaving || !dealForm.title || !selectedClient}>
            {dealSaving ? "Saving…" : editingDealId ? "Update Deal" : "Save Deal"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client create / edit dialog */}
      <ClientFormDialog
        open={clientFormOpen}
        onClose={() => setClientFormOpen(false)}
        client={editingClient}
        onSuccess={() => {
          setClientFormOpen(false);
          loadClients();
        }}
      />

      {/* Delete client dialog */}
      <DeleteClientDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        client={deletingClient}
        onSuccess={() => {
          setDeleteDialogOpen(false);
          setSelectedClientId(null);
          loadClients();
        }}
      />
    </Box>
  );
}
